import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import ApiService from "../apiService/ApiService";
import { LoginApi } from "../utils/urlConst";
import CommonLoader from "../components/CommonLoader";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import { useAlert } from "../contexts/AlertContext";
import AppLogoContainer from "../components/UserModule/AppLogoContainer";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { useTranslation } from "react-i18next";
import useResponsive from "../hooks/useResponsive";
import CommonTextField, { ErrorMessage } from "../components/CommonTextField";
import CommonButton from "../components/CommonButton";
import { useTimerContext } from "../contexts/TimerContext";
import { useProjectContext } from "../contexts/ProjectContext";
import { getMetaInfo } from "../utils/metaInfo";
import ExecutionAppIcon from "../assets/executionapp_icon.svg?react";

interface FormData {
  email: string;
  password: string;
}

export default function LoginScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { windowHeight } = useWindowDimensions();
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { setIsAuthenticated, isAuthenticated } = useTimerContext()
  const { clearProject } = useProjectContext();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    clearSavedData();
    try {
      const jsondata = JSON.stringify({
        email: data.email.trim(),
        password: data.password,
        is_from_mobile: true,
        meta_info: await getMetaInfo(),
      });

      const responseJson = await ApiService.post<any>(LoginApi, jsondata);

      if (responseJson?.status_code === 1) {
        LocalStorageUtil.setItem(storageKeys.isAuthenticated, true);
        setIsAuthenticated(true);
        showAlert(t("login.Login_success"), "success");
        LocalStorageUtil.setItem(
          storageKeys.authToken,
          responseJson.data.auth_token
        );
        LocalStorageUtil.setItem(
          storageKeys.workerId,
          responseJson.data.worker_id
        );
        LocalStorageUtil.setItem(storageKeys.userId, responseJson.data.id);
        LocalStorageUtil.setItem(
          storageKeys.emailId,
          responseJson.data.email_address
        );
        LocalStorageUtil.setItem(
          storageKeys.userName,
          responseJson.data.username
        );
        navigate("/planning");
      } else if (responseJson?.status_code === 0) {
        setErrorMessage(t("login.Username_Password_is_incorrect"));
      } else if (responseJson?.status_code === 2) {
        setErrorMessage(t("login.access_with_worker_credential"));
      } else {
        showAlert(t("login.Login_fail"), "error");
      }
    } catch (error) {
      showAlert(t("login.Login_fail"), "error");
    } finally {
      setLoading(false);
    }
  };
  const clearSavedData = () => {
    clearProject();
    localStorage.removeItem("isAuthenticated");
    setIsAuthenticated(false);
    LocalStorageUtil.clear();
  };
  useEffect(() => {
    console.log("isAutheticated Login ****", isAuthenticated)
  }, [isAuthenticated])


  return (
    <Box
      sx={{
        height: isMobile ? "auto" : windowHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        overflow: "auto",
      }}
    >
      <CommonLoader isVisible={loading} />

      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems="center"
        justifyContent={isMobile ? "flex-start" : "space-evenly"}
        sx={{
          width: "100%",
          height: isMobile ? "100%" : isTablet ? "auto" : 350,
          // minHeight: isMobile ? windowHeight : 'auto',
        }}
      >
        <AppLogoContainer />
        <Box
          sx={{
            width: isMobile ? "90%" : isTablet ? "50%" : "30%",
            mt: isMobile || isTablet ? "80px" : "0px",
            mb: isMobile || isTablet ? "20px" : "0px",
            alignItems: "center",
            height: isMobile ? 330 : 300,
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={"38px"}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={"10px"}
                sx={{ mb: 3, mt: "48px" }}
              >
                <ExecutionAppIcon
                  style={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography
                  component="span"
                  fontFamily={"Nunito Sans Bold"}
                  sx={{ fontSize: "22px", lineHeight: 1 }}
                >
                  <Box
                    component="span"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    {t("login.AppName")}
                  </Box>
                  {" "}
                  <Box
                    component="span"
                    sx={{ color: "#2B2A5C" }}
                  >
                    {t("login.Login")}
                  </Box>
                </Typography>
              </Stack>
              <Stack spacing={"18px"}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: t("login.Email_is_required"),
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: t("login.Email_is_invalid"),
                    },
                  }}
                  render={({ field }) => (
                    <CommonTextField
                      {...field}
                      placeholder={t("login.Email")}
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      icon={"/assets/user.svg"}
                      name={"user"}
                      onChange={(e) => field.onChange(e.target.value.trim())}
                      fontSize={"12px"}
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  rules={{ required: t("login.Password_is_required") }}
                  render={({ field }) => (
                    <CommonTextField
                      {...field}
                      placeholder={t("login.pass")}
                      fullWidth
                      error={!!errors.password}
                      helperText={
                        errors.password ? errors.password.message : ""
                      }
                      icon={"/assets/password.svg"}
                      name={"password"}
                      fontSize={"12px"}
                      isPassword={true}
                    />
                  )}
                />
                {errorMessage && (
                  <Typography
                    sx={{
                      color: "error.main",
                      mt: 1,
                      mb: 2,
                      fontSize: "12px",
                      fontFamily: "Nunito Sans 500",
                    }}
                  >
                    {errorMessage}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    mt: "8px",
                    mb: "14px",
                    fontSize: "13px",
                    fontFamily: "Nunito Sans 500",
                    color: theme.palette.greyColors.dark,
                  }}
                >
                  <span
                    onClick={() => navigate("/resetpasswordrequest")}
                    style={{
                      padding: '2px 4px',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      marginLeft: '17px',
                    }}
                    onMouseOver={e => (e.currentTarget.style.color = theme.palette.primary.main)}
                    onMouseOut={e => (e.currentTarget.style.color = theme.palette.greyColors.dark)}
                    onFocus={e => (e.currentTarget.style.color = theme.palette.primary.main)}
                    onBlur={e => (e.currentTarget.style.color = theme.palette.greyColors.dark)}
                  >
                    {t("login.forgot_password")}
                  </span>
                </Typography>
                <CommonButton
                  translationKey={"login.LogIn_Button"}
                  type="submit"
                />
              </Stack>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Box>
  );
}
